import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, Tab, Tabs } from 'react-bootstrap';
import { Plus, Trash, Save, ArrowClockwise } from 'react-bootstrap-icons';
import { SimpleSection } from '../components/Section';
import { clearTracksCache } from '../services/tracksService';
import config from '../config';

const AdminTracksPage = () => {
  // Tracks state
  const [tracks, setTracks] = useState(['Track 1', 'Track 2', 'Track 3']);
  const [originalTracks, setOriginalTracks] = useState([]);
  
  // Submission deadline state
  const [submissionDeadline, setSubmissionDeadline] = useState('');
  const [originalDeadline, setOriginalDeadline] = useState('');
  
  // Grading criteria state
  const [gradingCriteria, setGradingCriteria] = useState([
    { name: 'Technical Implementation', weight: 40, instructions: 'Evaluate the technical quality, code structure, and implementation approach.' },
    { name: 'Innovation & Creativity', weight: 30, instructions: 'Assess the originality and creative problem-solving approach.' },
    { name: 'Documentation & Presentation', weight: 20, instructions: 'Review the quality of documentation, clarity of explanation, and presentation.' },
    { name: 'Practical Impact', weight: 10, instructions: 'Consider the real-world applicability and potential impact of the solution.' }
  ]);
  const [originalCriteria, setOriginalCriteria] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastModified, setLastModified] = useState('');
  const [modifiedBy, setModifiedBy] = useState('');
  const [activeTab, setActiveTab] = useState('tracks');

  // Function to generate URL-friendly slug from track name
  const generateSlug = (trackName) => {
    if (!trackName) return '';
    return trackName
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, '');   // Remove leading/trailing hyphens
  };

  const fetchConfiguration = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch tracks
      const tracksResponse = await fetch(`${config.api.url}admin/tracks?format=json`);
      if (tracksResponse.ok) {
        const tracksData = await tracksResponse.json();
        const fetchedTracks = tracksData.tracks || ['Track 1', 'Track 2', 'Track 3'];
        setTracks([...fetchedTracks]);
        setOriginalTracks([...fetchedTracks]);
        setLastModified(tracksData.lastModified || '');
        setModifiedBy(tracksData.modifiedBy || '');
      }
      
      // Fetch challenge configuration (deadline and criteria)
      try {
        const configResponse = await fetch(`${config.api.url}admin/challenge-config?format=json`);
        if (configResponse.ok) {
          const configData = await configResponse.json();
          
          // Set submission deadline - convert from UTC to local datetime-local format
          if (configData.submissionDeadline) {
            const utcDate = new Date(configData.submissionDeadline);
            const localDatetime = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000)
              .toISOString().slice(0, 16);
            setSubmissionDeadline(localDatetime);
            setOriginalDeadline(localDatetime);
          }
          
          // Set grading criteria
          if (configData.gradingCriteria && configData.gradingCriteria.length > 0) {
            setGradingCriteria(configData.gradingCriteria);
            setOriginalCriteria(JSON.parse(JSON.stringify(configData.gradingCriteria)));
          } else {
            const defaultCriteria = [
              { name: 'Technical Implementation', weight: 40, instructions: 'Evaluate technical quality and implementation.' },
              { name: 'Innovation & Creativity', weight: 30, instructions: 'Assess innovative approaches and creative solutions.' },
              { name: 'Practical Impact', weight: 30, instructions: 'Consider real-world applicability and potential impact.' }
            ];
            setGradingCriteria(defaultCriteria);
            setOriginalCriteria(JSON.parse(JSON.stringify(defaultCriteria)));
          }
        } else {
          // Use defaults if config doesn't exist yet
          const defaultCriteria = [
            { name: 'Technical Implementation', weight: 40, instructions: 'Evaluate technical quality and implementation.' },
            { name: 'Innovation & Creativity', weight: 30, instructions: 'Assess innovative approaches and creative solutions.' },
            { name: 'Practical Impact', weight: 30, instructions: 'Consider real-world applicability and potential impact.' }
          ];
          setGradingCriteria(defaultCriteria);
          setOriginalCriteria(JSON.parse(JSON.stringify(defaultCriteria)));
        }
      } catch (configErr) {
        const defaultCriteria = [
          { name: 'Technical Implementation', weight: 40, instructions: 'Evaluate technical quality and implementation.' },
          { name: 'Innovation & Creativity', weight: 30, instructions: 'Assess innovative approaches and creative solutions.' },
          { name: 'Practical Impact', weight: 30, instructions: 'Consider real-world applicability and potential impact.' }
        ];
        setGradingCriteria(defaultCriteria);
        setOriginalCriteria(JSON.parse(JSON.stringify(defaultCriteria)));
      }
      
    } catch (err) {
      console.error('Error fetching configuration:', err);
      setError('Failed to load challenge configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  // Track management functions
  const handleTrackChange = (index, value) => {
    const newTracks = [...tracks];
    newTracks[index] = value;
    setTracks(newTracks);
  };

  const addTrack = () => {
    setTracks([...tracks, `Track ${tracks.length + 1}`]);
  };

  const removeTrack = (index) => {
    if (tracks.length > 1) {
      const newTracks = tracks.filter((_, i) => i !== index);
      setTracks(newTracks);
    }
  };

  // Grading criteria management functions
  const handleCriteriaChange = (index, field, value) => {
    const newCriteria = [...gradingCriteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setGradingCriteria(newCriteria);
  };

  const addCriteria = () => {
    setGradingCriteria([...gradingCriteria, {
      name: 'New Criteria',
      weight: 0,
      instructions: 'Enter instructions for this criteria.'
    }]);
  };

  const removeCriteria = (index) => {
    if (gradingCriteria.length > 1) {
      const newCriteria = gradingCriteria.filter((_, i) => i !== index);
      setGradingCriteria(newCriteria);
    }
  };

  // Validation functions
  const validateTracks = () => {
    return tracks.every(track => track.trim().length > 0);
  };

  const validateCriteria = () => {
    const totalWeight = gradingCriteria.reduce((sum, criteria) => sum + parseInt(criteria.weight || 0), 0);
    const allNamed = gradingCriteria.every(criteria => criteria.name.trim().length > 0);
    const allWeighted = gradingCriteria.every(criteria => criteria.weight > 0);
    
    return {
      valid: totalWeight === 100 && allNamed && allWeighted,
      totalWeight,
      allNamed,
      allWeighted
    };
  };

  const validateDeadline = () => {
    if (!submissionDeadline) return true; // Optional field
    const deadline = new Date(submissionDeadline);
    return deadline > new Date();
  };

  // Save functions
  const saveTracks = async () => {
    if (!validateTracks()) {
      setError('All track names must be filled in');
      return false;
    }

    try {
      const response = await fetch(`${config.api.url}admin/tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracks })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setOriginalTracks([...tracks]);
      await clearTracksCache();
      return true;
    } catch (err) {
      console.error('Error saving tracks:', err);
      throw new Error('Failed to save tracks configuration');
    }
  };

  const saveChallengeConfig = async () => {
    const criteriaValidation = validateCriteria();
    if (!criteriaValidation.valid) {
      if (!criteriaValidation.allNamed) {
        setError('All criteria must have names');
      } else if (!criteriaValidation.allWeighted) {
        setError('All criteria must have weights greater than 0');
      } else {
        setError(`Criteria weights must total 100% (currently ${criteriaValidation.totalWeight}%)`);
      }
      return false;
    }

    if (!validateDeadline()) {
      setError('Submission deadline must be in the future');
      return false;
    }

    try {
      const configData = {
        submissionDeadline: submissionDeadline ? new Date(submissionDeadline).toISOString() : null,
        gradingCriteria: gradingCriteria
      };

      const response = await fetch(`${config.api.url}admin/challenge-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setOriginalDeadline(submissionDeadline);
      setOriginalCriteria(JSON.parse(JSON.stringify(gradingCriteria)));
      return true;
    } catch (err) {
      console.error('Error saving challenge config:', err);
      throw new Error('Failed to save challenge configuration');
    }
  };

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await saveTracks();
      await saveChallengeConfig();
      
      setSuccess('Challenge configuration saved successfully!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const tracksChanged = JSON.stringify(tracks) !== JSON.stringify(originalTracks);
    const deadlineChanged = submissionDeadline !== originalDeadline;
    const criteriaChanged = JSON.stringify(gradingCriteria) !== JSON.stringify(originalCriteria);
    return tracksChanged || deadlineChanged || criteriaChanged;
  };

  const criteriaValidation = validateCriteria();
  const totalWeight = gradingCriteria.reduce((sum, criteria) => sum + parseInt(criteria.weight || 0), 0);

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading challenge configuration...</p>
        </div>
      </Container>
    );
  }

  return (<>
    {/* Header Section */}
    <SimpleSection>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h1>Challenge Configuration</h1>
          <p className="text-muted">Configure tracks, submission deadlines, and grading criteria</p>
        </div>
        <div>
          <Button 
            variant="outline-secondary" 
            onClick={fetchConfiguration}
            disabled={loading}
            className="me-2"
          >
            <ArrowClockwise size={16} className="me-1" />
            Refresh
          </Button>
          <Button 
            variant="primary" 
            onClick={saveAll}
            disabled={saving || !hasUnsavedChanges()}
          >
            <Save size={16} className="me-1" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {hasUnsavedChanges() && (
        <Alert variant="warning">
          <strong>Unsaved Changes:</strong> You have unsaved changes. Don't forget to save!
        </Alert>
      )}
    </SimpleSection>

    {/* Configuration Tabs Section */}
    <SimpleSection>
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        {/* Tracks Tab */}
        <Tab eventKey="tracks" title="Challenge Tracks">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Challenge Tracks</h5>
                <Button variant="outline-primary" size="sm" onClick={addTrack}>
                  <Plus size={16} className="me-1" />
                  Add Track
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Configure the available challenge tracks that participants can choose from when submitting solutions.
              </p>
              
              {tracks.map((track, index) => (
                <div key={index} className="mb-3">
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="text"
                      value={track}
                      onChange={(e) => handleTrackChange(index, e.target.value)}
                      placeholder={`Track ${index + 1} name`}
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => removeTrack(index)}
                      disabled={tracks.length <= 1}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                  <div className="ps-2">
                    <Form.Text className="text-muted">
                      URL slug: <code>/page/tracks/{generateSlug(track) || 'track-slug'}</code>
                    </Form.Text>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Tab>

        {/* Submission Deadline Tab */}
        <Tab eventKey="deadline" title="Submission Deadline">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Submission Deadline</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Set a deadline after which the submission form will be disabled. Leave empty for no deadline.
              </p>
              
              <Form.Group className="mb-3">
                <Form.Label>Submission Deadline</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={submissionDeadline}
                  onChange={(e) => setSubmissionDeadline(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Submissions will be disabled at this date/time. Time is in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}). Leave empty for no deadline.
                </Form.Text>
              </Form.Group>

              {submissionDeadline && (
                <Alert variant="info">
                  <strong>Deadline Preview:</strong> Submissions will be disabled after{' '}
                  {new Date(submissionDeadline).toLocaleString()} ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                  {new Date(submissionDeadline) <= new Date() && (
                    <span className="text-danger"> (This deadline has already passed!)</span>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Grading Criteria Tab */}
        <Tab eventKey="criteria" title="Grading Criteria">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Grading Criteria</h5>
                  <small className="text-muted">
                    Total Weight: {totalWeight}% 
                    <Badge bg={totalWeight === 100 ? 'success' : 'warning'} className="ms-2">
                      {totalWeight === 100 ? 'Valid' : 'Must equal 100%'}
                    </Badge>
                  </small>
                </div>
                <Button variant="outline-primary" size="sm" onClick={addCriteria}>
                  <Plus size={16} className="me-1" />
                  Add Criteria
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                Define the criteria used for grading submissions. Each criteria will appear in the review interface 
                with a 1-5 star rating and comment field.
              </p>

              {gradingCriteria.map((criteria, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h6>Criteria {index + 1}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeCriteria(index)}
                        disabled={gradingCriteria.length <= 1}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>

                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>Criteria Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={criteria.name}
                            onChange={(e) => handleCriteriaChange(index, 'name', e.target.value)}
                            placeholder="e.g., Technical Implementation"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Weight (%)</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            value={criteria.weight}
                            onChange={(e) => handleCriteriaChange(index, 'weight', parseInt(e.target.value) || 0)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group>
                      <Form.Label>Instructions for Reviewers</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={criteria.instructions}
                        onChange={(e) => handleCriteriaChange(index, 'instructions', e.target.value)}
                        placeholder="Provide guidance on how reviewers should evaluate this criteria..."
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              ))}

              {!criteriaValidation.valid && (
                <Alert variant="warning">
                  <strong>Validation Issues:</strong>
                  <ul className="mb-0 mt-2">
                    {!criteriaValidation.allNamed && <li>All criteria must have names</li>}
                    {!criteriaValidation.allWeighted && <li>All criteria must have weights greater than 0</li>}
                    {criteriaValidation.totalWeight !== 100 && (
                      <li>Total weight must equal 100% (currently {criteriaValidation.totalWeight}%)</li>
                    )}
                  </ul>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </SimpleSection>

    {/* Footer Section */}
    {lastModified && (
      <SimpleSection>
        <div className="text-muted small">
          Last modified: {lastModified} {modifiedBy && `by ${modifiedBy}`}
        </div>
      </SimpleSection>
    )}
  </>);
};

export default AdminTracksPage;
